import jwt_decode from 'jwt-decode';

/**
 * TA-10717: Apple returns `email` and `fullName` only on the FIRST
 * authorization of an Apple ID for a given bundle id. Every later sign-in —
 * a reinstall, a new device, or a re-signup after the TaskTag account was
 * deleted — hands back `email: null, fullName: null`, and in exactly those
 * cases the local `appleUserInfo` cache is gone too (deleting the account runs
 * `AsyncStorage.clear()`). Only the `identityToken` survives every sign-in.
 *
 * This module holds the pure normalization of those three sources so the
 * onboarding modal is left with a single call.
 *
 * Every field arrives untyped in practice — an `any` payload, a JSON blob out
 * of AsyncStorage, or a decoded JWT — so nothing here calls a string method on
 * a value it has not checked. A cache entry corrupted to `{"email": 1}` must
 * normalize to "absent", not throw halfway through sign-in.
 */

/** Coerces an unchecked value to a usable string, or `''`. */
export const asTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** Claims read from Apple's `identityToken`; a JWT can carry any shape. */
export type AppleIdentityTokenClaims = {
  sub?: unknown;
  email?: unknown;
};

/**
 * The fields read off `AppleAuthenticationCredential` — also the shape of the
 * credential cached in AsyncStorage, which is a serialized one.
 */
export type AppleCredentialLike = {
  user?: unknown;
  email?: unknown;
  fullName?: {
    givenName?: unknown;
    familyName?: unknown;
  } | null;
  identityToken?: unknown;
};

/** The payload the onboarding flow passes around for a social sign-in. */
export type NormalizedAppleUser = {
  id: string;
  email: string;
  given_name: string;
  family_name: string;
  provider: 'Apple';
  identity_token: string;
};

/**
 * `jwt_decode` throws on a malformed token. That rejection used to take down
 * the whole sign-in and land in a bare `console.log`, so the button just went
 * quiet — decoding defensively keeps the uid/email fallbacks optional.
 */
export const decodeAppleIdentityToken = (
  token?: string | null
): AppleIdentityTokenClaims | null => {
  if (!token) return null;
  try {
    return jwt_decode<AppleIdentityTokenClaims>(token) ?? null;
  } catch {
    return null;
  }
};

/** Tolerates a missing or corrupted cache entry instead of throwing. */
export const parseCachedAppleCredential = (
  cached?: string | null
): AppleCredentialLike | null => {
  if (!cached) return null;
  try {
    const parsed: unknown = JSON.parse(cached);
    // `typeof [] === 'object'`, so arrays have to be excluded explicitly or
    // a `'[]'` entry would flow on as a truthy-but-wrong credential.
    return typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as AppleCredentialLike)
      : null;
  } catch {
    return null;
  }
};

/**
 * Picks each field from the live credential first, then the cache, then the
 * identity token. Per candidate rather than per field, so a corrupted cache
 * value cannot shadow a usable token claim just by being truthy.
 */
export const normalizeAppleUser = (
  credential: AppleCredentialLike,
  cached: AppleCredentialLike | null,
  claims: AppleIdentityTokenClaims | null
): NormalizedAppleUser => ({
  id:
    asTrimmedString(credential.user) ||
    asTrimmedString(cached?.user) ||
    asTrimmedString(claims?.sub),
  email:
    asTrimmedString(credential.email) ||
    asTrimmedString(cached?.email) ||
    asTrimmedString(claims?.email),
  given_name:
    asTrimmedString(credential.fullName?.givenName) ||
    asTrimmedString(cached?.fullName?.givenName),
  family_name:
    asTrimmedString(credential.fullName?.familyName) ||
    asTrimmedString(cached?.fullName?.familyName),
  // TA-10975: unlike the other fields, this never falls back to the cache or
  // claims — a cached identityToken is from a previous sign-in and would be
  // expired, and the claims are only its own decoded contents. Only the live
  // credential's token is a proof the backend can still verify.
  identity_token: asTrimmedString(credential.identityToken),
  provider: 'Apple',
});

/** Safe-to-log shape for a verify_user "no existing account" event — no raw email or raw backend error, so it can go straight into Sentry extras. */
export type AppleVerifyMissContext = {
  uid: string;
  provider: string;
  hasEmail: boolean;
  emailDomain: string;
  error: string;
};

/**
 * TA-8178: the verify_user "no existing account" branch fires for every
 * genuine new Apple signup as well as the (unconfirmed) false-negative case
 * reported against an existing account, so it needs to log enough to compare
 * against a specific user report without becoming a second PII sink. The
 * email is reduced to presence + domain, and the backend's `error` is only
 * kept when it is already a plain string (never an object dump).
 */
export const buildAppleVerifyMissContext = (
  uid: unknown,
  provider: unknown,
  email: unknown,
  responseError: unknown
): AppleVerifyMissContext => {
  const trimmedEmail = asTrimmedString(email);
  return {
    uid: asTrimmedString(uid),
    provider: asTrimmedString(provider),
    hasEmail: trimmedEmail !== '',
    emailDomain: trimmedEmail.split('@')[1] ?? '',
    error: typeof responseError === 'string' ? responseError.slice(0, 200) : '',
  };
};

/** "john.doe+work" → "John Doe Work" */
const nameFromEmailLocalPart = (email: string): string =>
  email
    .split('@')[0]
    .split(/[._+\-\s]+/)
    .filter((word) => word !== '')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

/**
 * The name the Apple registration payload must carry.
 *
 * The register endpoint validates `name` for presence, and the previous inline
 * expression — `[given, family].filter((item) => item !== '').join(' ')` —
 * kept `undefined` (it is not `''`) while `join` rendered it as an empty
 * string, so a repeat authorization posted `name: ' '`: a single space, which
 * Rails treats as blank. The failure then surfaced as the generic
 * "error creating account." alert.
 *
 * Falling back to the email keeps the account creatable. The value is a
 * placeholder by design: `signUpWithAppleRegister` navigates straight to the
 * register screen, whose name fields start empty and overwrite this through
 * `updateMe` as soon as the user submits.
 *
 * Guarantees a non-empty result whenever `email` is a non-blank string, so the
 * caller only has to guard the missing-email case.
 */
export const buildAppleDisplayName = (
  givenName?: unknown,
  familyName?: unknown,
  email?: unknown
): string => {
  const fromCredential = [givenName, familyName]
    .map((part) => asTrimmedString(part))
    .filter((part) => part !== '')
    .join(' ');
  if (fromCredential !== '') return fromCredential;

  const trimmedEmail = asTrimmedString(email);
  if (trimmedEmail === '') return '';

  return nameFromEmailLocalPart(trimmedEmail) || trimmedEmail;
};
